import uniqueId from "lodash/uniqueId";
import * as React from "react";
import { County, CountyZipAggregate, State, Zip } from "@app/geography";
import { Link } from "react-router-dom";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { breadcrumbTemplates, BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderLeft from "@app/shared/PageHeaderLeft";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import GeographyService from "@app/geography/geographyService";

interface ZipDetailsProps {
  children?: React.ReactNode;
  lookupZip: string;
}

interface ZipPaneState {
  activeState?: State;
  activeZip?: Zip;
  counties: County[];
  states: State[];
  zips: Zip[];
}

export default class GeoLookupZipView extends React.Component<ZipDetailsProps, ZipPaneState> {
  private readonly geographyService = new GeographyService();
  private readonly breadcrumbs = breadcrumbTemplates[BreadcrumbTemplateEnum.SearchZipCode];

  constructor(props: ZipDetailsProps) {
    super(props);

    this.state = {
      counties: [],
      states: [],
      zips: [],
    };
  }

  public async componentDidMount() {
    if (this.props.lookupZip.length > 0) {
      const states = await this.geographyService.getStates();
      this.setState((prevState) => ({ ...prevState, states }));

      await this.getZip(this.props.lookupZip);
    }
  }

  public render() {
    let citiesListPrimary;
    let citiesListOther;
    let countiesListPrimary;
    let countiesListOther;

    if (this.state.activeZip) {
      const cities = this.state.zips.filter((zip) => zip.zipCode === this.state.activeZip.zipCode);
      citiesListPrimary = cities.map((zip) => {
        if (zip.isPrimaryCounty || cities.length === 1) {
          return (<li className="mtb5 plr10" key={uniqueId("primary-" + zip.name)}><strong>{zip.name}, {this.state.activeState.abbreviation}</strong></li>);
        }
      });

      const citiesOther = this.state.zips.filter((zip) => zip.zipCode === this.state.activeZip.zipCode);
      citiesListOther = citiesOther.map((zip) => {
        if (!zip.isPrimaryCounty && cities.find((e) => e.zipCode !== zip.zipCode)) {
          return (<li className="mtb5 plr10" key={uniqueId(zip.name)}>{zip.name}, {this.state.activeState.abbreviation}</li>);
        }
      });

      countiesListPrimary = this.state.counties.map((county) => {
        if (county.countySSA === this.state.activeZip.primaryCountySSA) {
          return (<li className="mtb5 plr10" key={uniqueId("primary-" + county.name)}><strong><Link to={`/tools/lookupcounty/${county.state}/${county.countySSA}`}>{county.name}, {county.state}</Link></strong></li>);
        }
      });

      countiesListOther = this.state.counties.map((county) => {
        if (county.countySSA !== this.state.activeZip.primaryCountySSA) {
          return (<li className="mtb5 plr10" key={uniqueId(county.name)}><Link to={`/tools/lookupcounty/${county.state}/${county.countySSA}`}>{county.name}, {county.state}</Link></li>);
        }
      });
    }

    return (
      <>
        {(this.state.activeZip) && (
          <Page documentTitle="Lookup a Zip Code">
            <Breadcrumbs items={this.breadcrumbs} />
            <PageInner>
              <PageHeader title={this.state.activeZip.zipCode} disableHR={true} iconName={FontAwesome.Search}>
                <PageHeaderLeft>
                  <span className="geolookup-subtitle">{this.state.activeZip.name}, {this.state.activeState.abbreviation}</span>
                </PageHeaderLeft>
                <PageHeaderRight>
                  <div className="page-title-bar">
                    <div className="ms-auto">
                      <div className="d-block d-md-inline-block">
                        <input type="text" className="form-control" maxLength={5} minLength={5} placeholder="Type Zip Here" onChange={this.handleZipChange} />
                      </div>
                    </div>
                  </div>
                </PageHeaderRight>
              </PageHeader>
              <PageSection>
                <div className="row">
                  <div className="col-12 col-sm-4">
                    <div className="card card-expansion-item card-static">
                      <div className="card-header">Zip Details</div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-12">
                            <p><strong>Enclosing Zip:</strong> {this.state.activeZip.enclosingZip || "N/A"} </p>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-12">
                            <p><strong>Area:</strong> {this.state.activeZip.area} mi<sup>2</sup></p>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-12">
                            <div className="row">
                              <div className="col-12">
                                <strong>Extents:</strong>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <em className="ps-3">Minimum Latitude:</em> {this.state.activeZip.minLatitude}
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <em className="ps-3">Minimum Longitude:</em> {this.state.activeZip.minLongitude}
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <em className="ps-3">Maximum  Latitude:</em> {this.state.activeZip.maxLatitude}
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <p className="ps-3"><em>Maximum  Longitude:</em> {this.state.activeZip.maxLongitude}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-12">
                            <div className="row">
                              <div className="col-12">
                                <strong>Centroid:</strong>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <em className="ps-3">Latitude:</em> {this.state.activeZip.centroidLatitude}
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <p className="ps-3"><em>Longitude:</em> {this.state.activeZip.centroidLongitude}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-12">
                            <div className="row">
                              <div className="col-12">
                                <strong>Label:</strong>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <em className="ps-3">Latitude:</em> {this.state.activeZip.labelLatitude}
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12">
                                <em className="ps-3">Longitude:</em> {this.state.activeZip.labelLongitude}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-8">
                    <div className="card card-expansion-item card-static">
                      <div className="card-header">Cities</div>
                      <div className="card-body">
                        <ul className="list-unstyled mb0">
                          {citiesListPrimary}
                          {citiesListOther}
                        </ul>
                      </div>
                    </div>
                    <div className="card card-expansion-item card-static">
                      <div className="card-header">Counties</div>
                      <div className="card-body">
                        <ul className="list-unstyled mb0">
                          {countiesListPrimary}
                          {countiesListOther}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </PageSection>
            </PageInner>
          </Page>
        )}
      </>
    );
  }

  private async getZip(zip: string) {
    let activeState: State;
    let activeZip: Zip;
    const zipsAndCounties: CountyZipAggregate = await this.geographyService.getCountyZipAggregateByZip(zip);
    const counties: County[] = zipsAndCounties.counties;
    const zips: Zip[] = zipsAndCounties.zips;

    if (counties.length > 0) {
      activeZip = zips.find((e) => e.zipCode === zip);
      activeState = this.state.states.find((e) => e.abbreviation === (counties.find((f) => f.countySSA === activeZip.primaryCountySSA).state));

      this.setState((prevState) => ({ ...prevState, activeState, activeZip, counties, zips }));
    }
  }

  private handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const zip = e.target.value;
    //is it long enough to be a valid zip code (and is it numeric)
    if (zip.length === 5 && !isNaN(+zip)) {
      this.getZip(zip);
    }
  }
}

export { GeoLookupZipView };
